/**
 * -----------------------------------------------------------------------------
 * Category Admin Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single geozone category of the organization.
 */

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useAssignGeozoneToGeozoneCategoryMutation,
  useAssignUserToGeozoneCategoryMutation,
  useDeleteOrganizationGeozoneCategoryMutation,
  useEditOrganizationGeozoneCategoryMutation,
  useOrganizationGeozoneCategoriesQuery,
  useSingleOrganizationGeozoneCategoryQuery,
} from "../../../api/network/adminApiServices";
import { RESPONSE_CODE } from "../../../api/network/constants";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import {
  AssignGeozoneToGeozoneCategoryPayload,
  AssignUserToGeozoneCategoryPayload,
  EditGeozoneCategoryPayload,
  TLegacyGeozoneCategory,
} from "../../../api/types/GeozoneCategory";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import svgIcons from "../../../assets/svg/svgIcons";
import Accordian from "../../../components/accordian";
import FormActionButtons, {
  FormActionButton,
} from "../../../components/admin/formActionButtons";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLocalStorage } from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { useReportUtils } from "../../reports/hooks/reportUtils";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import {
  GCGeozonesForm,
  GCUsersForm,
  GCGeneralDetailForm,
} from "./geozone-category-form";
import {
  TFormFieldNames,
  geozoneCategoryDetailsInitialValues,
  geozoneCategoryDetailsYupValidationSchema,
} from "./validation";

const ScreenAdminDetailGeozoneCategory = () => {
  const { geozoneCategoryId } = useParams<{ geozoneCategoryId: string }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.geozone_categories",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.geozone_categories.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { refetchCustomReportListToStore } = useReportUtils();
  const { getUserACLAccessLevel } = useACL();

  const thisUserTimezone = useLoggedInUserData("timezone");

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { geozoneCategories: orgGeozoneCategoriesQueryParams } =
    listingQueryParams;

  const { getLocalStorageItem, setLocalStorageItem } = useLocalStorage();
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );

  // flag to idenfiy if user is coming from create new user popup
  const isNewEntity = React.useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );

  // prepare query params for fetching organization users
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        geozoneCategories: {
          ...orgGeozoneCategoriesQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  // fetch organization geozone categories
  const {
    data: dataOrgGeozoneCategories,
    isFetching: isFetchingOrgGeozoneCategories,
    error,
  } = useOrganizationGeozoneCategoriesQuery(orgGeozoneCategoriesQueryParams);

  // fetch single geozone category details
  const {
    data: dataSingleGeozoneCategory,
    isFetching: isFetchingSingleGeozoneCategory,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationGeozoneCategoryQuery(
    {
      categoryid: `${geozoneCategoryId}`,
    },
    {
      skip: !geozoneCategoryId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
          RESPONSE_CODE.NOT_FOUND ||
        (errorSingleEntity as FetchBaseQueryError)?.status ===
          RESPONSE_CODE.BAD_REQUEST
      ) {
        toast.error(t("toast.category_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.geozoneCategories);
      }
    }
  }, [isErrorSingleEntity, errorSingleEntity]);

  // geozone category mutations
  const [
    editGeozoneCategoryApiTrigger,
    { isLoading: isLoadingEditGeozoneCategory },
  ] = useEditOrganizationGeozoneCategoryMutation();
  const [
    deleteGeozoneCategoryApiTrigger,
    { isLoading: isLoadingDeleteGeozoneCategory },
  ] = useDeleteOrganizationGeozoneCategoryMutation();
  const { results, count } = dataOrgGeozoneCategories || {};

  const [
    editCategoryGeozonesTrigger,
    { isLoading: isLoadingEditCategoryGeozones },
  ] = useAssignGeozoneToGeozoneCategoryMutation();
  const [editCategoryUsersTrigger, { isLoading: isLoadingEditCategoryUsers }] =
    useAssignUserToGeozoneCategoryMutation();
  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(!isFetchingSingleGeozoneCategory);
  }, [isFetchingSingleGeozoneCategory]);

  const formik = useFormik({
    initialValues: geozoneCategoryDetailsInitialValues,
    validationSchema: geozoneCategoryDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      const {
        accountid,
        creationtime,
        lastupdatetime,
        users,
        geozones,
        ...singleGeozoneCategory
      } = dataSingleGeozoneCategory as TLegacyGeozoneCategory;
      const preparedGeozoneCategoryEditParam: EditGeozoneCategoryPayload = {
        ...(singleGeozoneCategory as EditGeozoneCategoryPayload),
        displayname: values.displayname,
        description: values.description,
        notes: values.notes,
      };

      // fire all APIs concurrently - 1 of 3
      const updateGeozoneCategoryPromise = editGeozoneCategoryApiTrigger(
        preparedGeozoneCategoryEditParam
      ).unwrap();

      // prepare payload for assigning geozones
      const preparedAssignGeozonesParam: AssignGeozoneToGeozoneCategoryPayload =
        {
          categoryid: values.categoryid,
          geozoneid: (values.geozones || [])
            .map((geozone) =>
              typeof geozone === "string" ? geozone : geozone.value
            )
            .join(","),
        };

      // fire all APIs concurrently - 2 of 3
      const updateCategoryGeozonesPromise = editCategoryGeozonesTrigger(
        preparedAssignGeozonesParam
      ).unwrap();

      // prepare payload for assigning users
      const preparedAssignUsersParam: AssignUserToGeozoneCategoryPayload = {
        categoryid: values.categoryid,
        userid: (values.users || [])
          .map((user) => (typeof user === "string" ? user : user.value))
          .join(","),
      };

      // fire all APIs concurrently - 3 of 3
      const updateCategoryUsersPromise = editCategoryUsersTrigger(
        preparedAssignUsersParam
      ).unwrap();

      Promise.all([
        updateGeozoneCategoryPromise,
        updateCategoryGeozonesPromise,
        updateCategoryUsersPromise,
      ])
        .then(([userData, aclData]) => {
          // Both succeeded
          toast.success(t("toast.category_updated"));
          navigate(routeUrls.dashboardChildren.adminChildren.geozoneCategories);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (dataSingleGeozoneCategory) {
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        categoryid: dataSingleGeozoneCategory?.categoryid,
        displayname: dataSingleGeozoneCategory?.displayname,
        description: dataSingleGeozoneCategory?.description,
        notes: dataSingleGeozoneCategory?.notes,
        geozones:
          dataSingleGeozoneCategory?.geozones?.map((item) => ({
            label: `${item}`,
            value: `${item}`,
          })) || [],
        users:
          dataSingleGeozoneCategory?.users?.map((item) => ({
            label: `${item}`,
            value: `${item}`,
          })) || [],
      });
      setUserCanEdit(!!isNewEntity?.current);
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [dataSingleGeozoneCategory, geozoneCategoryId]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
  } = formik;

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  // handle edit user
  const handleEditUser = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  // handle delete geozone category
  const handleDeleteGeozoneCategory = () => {
    deleteGeozoneCategoryApiTrigger({
      categoryid: dataSingleGeozoneCategory?.categoryid ?? "",
    })
      .unwrap()
      .then(() => {
        toast.success(t("toast.category_deleted"));
        navigate(routeUrls.dashboardChildren.adminChildren.geozoneCategories);
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
      });
  };

  const onBackButtonClick = () => {
    navigate(routeUrls.dashboardChildren.adminChildren.geozoneCategories);
  };
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "danger",
              label: tMain("delete"),
              onClick: () => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showDeleteConfirmation: true,
                  })
                );
              },
              disabled:
                isFetchingSingleGeozoneCategory ||
                isLoadingEditGeozoneCategory ||
                isLoadingEditCategoryGeozones ||
                isLoadingEditCategoryUsers ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) <
                  ROLE_ACCESS_LEVELS.ALL,
            },
            ...(userCanEdit
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "secondary",
                    label: tMain("cancel"),
                    onClick: () => onBackButtonClick(),
                  } as FormActionButton,
                ]
              : []),
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleEditUser
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isFetchingSingleGeozoneCategory ||
                isLoadingEditGeozoneCategory ||
                isLoadingEditCategoryGeozones ||
                isLoadingEditCategoryUsers ||
                (userCanEdit && !!Object.keys(errors).length) ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) <
                  ROLE_ACCESS_LEVELS.WRITE,
            },
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isFetchingSingleGeozoneCategory,
    isLoadingEditGeozoneCategory,
    isLoadingEditCategoryGeozones,
    isLoadingEditCategoryUsers,
    userCanEdit,
    errors,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditUser(),
              disabled:
                isLoadingEditGeozoneCategory ||
                isLoadingEditCategoryGeozones ||
                isLoadingEditCategoryUsers ||
                !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [
    isLoadingEditGeozoneCategory,
    isLoadingEditCategoryGeozones,
    isLoadingEditCategoryUsers,
    errors,
  ]);
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center">
          <p className="font-semibold text-accent-blue-dark text-lg leading-6">
            {t("sub_heading")}
          </p>
        </div>
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="absolute right-60 h-48 overflow-auto text-[8px]">
            {JSON.stringify(values, null, 2)}
          </pre>
        )}
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingOrgGeozoneCategories ? " opacity-40 pointer-events-none" : ""}${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgGeozoneCategoriesQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {dataOrgGeozoneCategories?.results?.map(
                (item: TLegacyGeozoneCategory, index: number) => (
                  <AdminListingColumnItem
                    key={index}
                    selected={geozoneCategoryId === item.categoryid}
                    onClick={() =>
                      navigate(
                        `${routeUrls.dashboardChildren.adminChildren.geozoneCategories}/${item.categoryid}`
                      )
                    }
                    title={item.displayname}
                    description={item.description}
                    asideText={item.categoryid}
                    bottomText={t("totals", {
                      geozone_count: item?.geozones?.length || 0,
                      user_count: item?.users?.length || 0,
                    })}
                  />
                )
              )}
            </div>
            {!isFetchingOrgGeozoneCategories && (
              <Pagination
                pageSize={orgGeozoneCategoriesQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      geozoneCategories: {
                        ...orgGeozoneCategoriesQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(
                        count / orgGeozoneCategoriesQueryParams.page_size
                      )
                    : 1
                }
                forcePage={orgGeozoneCategoriesQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      geozoneCategories: {
                        ...orgGeozoneCategoriesQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {isFetchingSingleGeozoneCategory && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditUser}
                      disabled={
                        isLoadingEditGeozoneCategory || !!Object.keys(errors).length
                      }
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {isFetchingSingleGeozoneCategory && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {MemoizedFormActionButtons}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleGeozoneCategory || isLoadingEditGeozoneCategory || isLoadingDeleteGeozoneCategory || isLoadingEditCategoryGeozones || isLoadingEditCategoryUsers ? "opacity-40" : ""}`}
            >
              <form onSubmit={handleSubmit}>
                <Accordian
                  title={t("accord_general_details")}
                  openByDefault={true}
                >
                  <GCGeneralDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleGeozoneCategory || !formikValuesReady
                    }
                  />
                </Accordian>
                {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) >=
                  ROLE_ACCESS_LEVELS.READ && (
                  <Accordian title={t("accord_geozones")}>
                    <GCGeozonesForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      formikSetValue={formik.setFieldValue}
                      handleBlur={handleBlur}
                      formikSetTouched={formik.setFieldTouched}
                      userCanEdit={
                        getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) >=
                          ROLE_ACCESS_LEVELS.WRITE && userCanEdit
                      }
                      loadingData={
                        isFetchingSingleGeozoneCategory || !formikValuesReady
                      }
                    />
                  </Accordian>
                )}
                {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) >=
                  ROLE_ACCESS_LEVELS.READ && (
                  <Accordian title={t("accord_users")}>
                    <GCUsersForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      formikSetValue={formik.setFieldValue}
                      handleBlur={handleBlur}
                      formikSetTouched={formik.setFieldTouched}
                      userCanEdit={
                        getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) >=
                          ROLE_ACCESS_LEVELS.WRITE && userCanEdit
                      }
                      loadingData={
                        isFetchingSingleGeozoneCategory || !formikValuesReady
                      }
                    />
                  </Accordian>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      <ModalDeleteConfirmation
        handleDeleteAdmin={handleDeleteGeozoneCategory}
      />
    </>
  );
};

export default ScreenAdminDetailGeozoneCategory;
